import type { ButtonModifier } from '@polycups/react'
import cx from 'classnames'

import { LIST_SIZE } from '@/blocks/constants'
import { ButtonLink } from '@/components/Button'
import { Container, type Space } from '@/components/Container'
import { HeadingM, HeadingS, SubHeading, BodyM } from '@/components/Typography'
import { decodeURL } from '@/core/utils/dom/decodeURL'
import type { TitleProps } from '@/models'

export interface TextBlockProps {
  id?: string
  title?: TitleProps
  subtitle?: TitleProps
  size: 'm' | 'l'
  space: Space
  spaceIn: Space
  content: string
  hasButton: boolean
  position: 'left' | 'right' | 'center'
  button?: {
    label: string
    link: string
    modifier: ButtonModifier
    ariaLabel?: string
    customGALabel?: string
  }
}

const TextBlock = ({
  id,
  size,
  title,
  subtitle,
  content,
  space,
  spaceIn,
  position = 'left',
  hasButton,
  button,
}: TextBlockProps) => {
  const TitleComponent = size === LIST_SIZE.lg ? HeadingM : HeadingS

  return (
    <Container
      spaceIn={spaceIn}
      spaceOut={space}
      id={id}
      className={cx({ [`et-ta-${position}`]: position })}
    >
      {title && title.text && <TitleComponent tag={title.tag || 'h3'}>{title.text}</TitleComponent>}
      {subtitle && subtitle.text && (
        <SubHeading dark="medium" className="eu-smt-2" tag={subtitle.tag || 'h4'}>
          {subtitle.text}
        </SubHeading>
      )}

      {content && <BodyM dark="medium" tag="div" className="eu-smt-2" html={content} />}

      {hasButton && button && (
        <div className="eu-smt-3">
          <ButtonLink
            event={{ label: button.customGALabel }}
            source={decodeURL(button.link)}
            modifier={button.modifier}
            aria-label={button.ariaLabel}
            size="lg"
          >
            {button.label}
          </ButtonLink>
        </div>
      )}
    </Container>
  )
}

TextBlock.size = LIST_SIZE

export default TextBlock
